import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Check } from 'lucide-react';
import { SignaturePreview } from './SignaturePreview';
import { SIGNATURE_SIZE_OPTIONS, SIGNATURE_SIZE_PRESETS, BANNER_SIZE_OPTIONS, DEFAULT_BANNER_SIZE, getBannerPresetWidth } from '../../utils/signature/sizeUtils';

const SAMPLE = {
  fullName: 'Jordan Avery',
  credentials: 'MBA',
  jobTitle: 'Director of Client Success',
  phoneNumber: '5551234567',
  mobileNumber: '5559876543',
  email: 'jordan@example.com',
  website: 'example.com',
  address: '321 Street Rd, Palo Alto, CA 94020',
  countryCode: '+1',
  linkedin: 'in/jordan-avery',
  xUsername: 'jordanavery',
};

const buildVisibility = (config) => {
  const visibility = {};
  Object.keys(config || {}).forEach((key) => {
    if (!key.startsWith('visible_')) return;
    const snake = key.replace('visible_', '');
    const camel = snake.replace(/_([a-z])/g, (_m, c) => c.toUpperCase());
    visibility[camel] = config[key];
  });
  return visibility;
};

export const SignatureSizeCompare = ({ open, onOpenChange, config = {}, company, currentSize = 'standard', currentBannerSize = DEFAULT_BANNER_SIZE, onApply }) => {
  const [zoom, setZoom] = useState(0.7);
  const [bannerSizes, setBannerSizes] = useState({});
  const visibilitySettings = buildVisibility(config);
  const bannerSizeFor = (size) => bannerSizes[size] || currentBannerSize;

  useEffect(() => {
    if (open) setBannerSizes({});
  }, [open]);

  const baseData = {
    ...SAMPLE,
    company: company?.name || 'Your Company',
    brandColor: config.brand_color || company?.brand_color || '#EB2F5B',
    profileImage: config.default_profile_image || '',
    companyLogo: config.company_logo || '',
    companyLogoLink: config.company_logo_link || '',
    bannerImage: config.banner_image || '',
    bannerLink: config.banner_link || '',
    disclaimer: config.disclaimer || '',
    bookMeeting: config.default_book_meeting || '',
    bookMeetingText: config.meeting_link_text || 'Book a Meeting',
    useBrandColorForSocial: config.use_brand_color_for_social_icons ?? false,
    showFieldLabels: config.show_field_labels !== false,
    fieldLabelsScope: config.field_labels_scope || (config.show_field_labels === false ? 'none' : 'all'),
    showCountryCode: config.show_country_code !== false,
    officeLabel: config.office_label ?? 'Office',
    mobileLabel: config.mobile_label ?? 'Mobile',
    labelStyle: config.label_style || 'brand',
    labelStyleScope: config.label_style_scope || 'phones',
    bannerWidth: null,
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[95vw] sm:max-w-[1400px] max-h-[90vh] overflow-y-auto" data-testid="size-compare-dialog">
        <DialogHeader>
          <DialogTitle>Compare signature sizes</DialogTitle>
          <DialogDescription>
            The same signature rendered at each preset, using your branding. Pick one to set it as the company default —
            you still need to hit Save Changes.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-end gap-2 text-sm text-gray-500">
          <span>Preview scale</span>
          {[0.5, 0.7, 1].map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setZoom(value)}
              data-testid={`size-compare-zoom-${Math.round(value * 100)}`}
              className={`px-2 py-1 rounded border text-xs transition-colors ${
                zoom === value ? 'border-[#EB2F5B] text-[#EB2F5B] bg-pink-50' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {Math.round(value * 100)}%
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-2">
          {SIGNATURE_SIZE_OPTIONS.map((option) => {
            const preset = SIGNATURE_SIZE_PRESETS[option.value];
            const isCurrent = currentSize === option.value;
            return (
              <div
                key={option.value}
                data-testid={`size-compare-column-${option.value}`}
                className={`rounded-lg border p-4 flex flex-col ${
                  isCurrent ? 'border-[#EB2F5B] ring-1 ring-[#EB2F5B] bg-pink-50/40' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-gray-900">{option.label}</p>
                    <p className="text-xs text-gray-500 mt-1">{option.description}</p>
                  </div>
                  {isCurrent ? (
                    <span
                      className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-[#EB2F5B]"
                      data-testid={`size-compare-current-${option.value}`}
                    >
                      <Check className="h-3 w-3" /> Current
                    </span>
                  ) : null}
                </div>

                <p className="text-xs text-gray-400 mt-2">
                  {preset.maxWidth}px wide · {preset.nameSize}px name · {preset.photoSize}px photo
                </p>

                <div className="mt-3">
                  <p className="text-xs font-medium text-gray-600 mb-1">Banner</p>
                  <div className="flex flex-wrap gap-1" role="radiogroup" aria-label={`${option.label} banner size`}>
                    {BANNER_SIZE_OPTIONS.map((banner) => {
                      const bannerActive = bannerSizeFor(option.value) === banner.value;
                      return (
                        <button
                          key={banner.value}
                          type="button"
                          role="radio"
                          aria-checked={bannerActive}
                          data-testid={`size-compare-banner-${option.value}-${banner.value}`}
                          onClick={() => setBannerSizes((prev) => ({ ...prev, [option.value]: banner.value }))}
                          className={`px-2 py-1 rounded border text-xs transition-colors ${
                            bannerActive
                              ? 'border-[#EB2F5B] text-[#EB2F5B] bg-pink-50'
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          {banner.label} · {getBannerPresetWidth(option.value, banner.value)}px
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-3 bg-white border rounded overflow-x-auto p-3 flex-1">
                  <div style={{ zoom }}>
                    <SignaturePreview
                      signatureData={{ ...baseData, signatureSize: option.value, bannerSize: bannerSizeFor(option.value) }}
                      visibilitySettings={visibilitySettings}
                      previewId={`signature-preview-compare-${option.value}`}
                      showVCard={false}
                    />
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t">
                  <Button
                    className="w-full"
                    size="sm"
                    variant={isCurrent ? 'outline' : 'default'}
                    data-testid={`size-compare-apply-${option.value}`}
                    onClick={() => onApply(option.value, bannerSizeFor(option.value))}
                  >
                    {isCurrent ? 'Keep this size' : 'Use this size'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SignatureSizeCompare;
